import React, { useState, useEffect } from 'react';
import { onGameEvent } from '../../core/GameEvents';
import { networkManager } from '../../systems/NetworkManager';
import type { BountyInfo } from '@astra/shared';
import { SHIPS, BOUNTY_CONFIG } from '@astra/shared';

const PALETTE = {
  DARK: '#0f1b26',
  DARK_TRANSPARENT: 'rgba(15, 27, 38, 0.95)',
  LIGHT: '#f5e8d1',
  TEAL: '#20a5a6',
  ORANGE: '#dd5639',
  DIM: '#4a5a6a',
  PANEL: '#1a2a38',
  PANEL_TRANSPARENT: 'rgba(26, 42, 56, 0.95)',
  GREEN: '#4ade80',
  RED: '#ef4444',
  YELLOW: '#fbbf24',
};

// Tier colors
function getTierColor(tier: number): string {
  switch (tier) {
    case 1: return PALETTE.GREEN;
    case 2: return '#3b82f6'; // Blue
    case 3: return PALETTE.ORANGE;
    case 4: return PALETTE.RED;
    default: return PALETTE.LIGHT;
  }
}

// Format credits with commas
function formatCredits(amount: number): string {
  return amount.toLocaleString();
}

// Format countdown timer as M:SS
function formatCountdown(ms: number): string {
  if (ms <= 0) return '0:00';
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

// Get ship name from class ID
function getShipName(shipClassId: string): string {
  const shipDef = SHIPS[shipClassId];
  return shipDef?.name || 'Unknown Ship';
}

// Get ship class type name
function getShipClassName(shipClassId: string): string {
  const shipDef = SHIPS[shipClassId];
  if (!shipDef) return 'Unknown';
  // Capitalize first letter of ship class
  const shipClass = shipDef.class || 'unknown';
  return shipClass.charAt(0).toUpperCase() + shipClass.slice(1);
}

interface BountyBoardModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function BountyBoardModal({ isOpen, onClose }: BountyBoardModalProps) {
  const [bounties, setBounties] = useState<BountyInfo[]>([]);
  const [lastUpdated, setLastUpdated] = useState<number>(Date.now());
  const [countdownText, setCountdownText] = useState<string>('5:00');

  // Request bounty list when modal opens
  useEffect(() => {
    if (isOpen) {
      networkManager.requestBountyList();
    }
  }, [isOpen]);

  // Listen for bounty list updates
  useEffect(() => {
    const unsubscribeList = onGameEvent('bountyListUpdate', (data) => {
      setBounties(data.bounties);
      setLastUpdated(data.timestamp);
    });

    const unsubscribeCleared = onGameEvent('bountyCleared', (data) => {
      // Remove cleared bounty from list
      setBounties((prev) => prev.filter((b) => b.playerId !== data.playerId));
    });

    return () => {
      unsubscribeList();
      unsubscribeCleared();
    };
  }, []);

  // Update countdown timer every second
  useEffect(() => {
    const updateCountdown = () => {
      const elapsed = Date.now() - lastUpdated;
      const remaining = BOUNTY_CONFIG.BROADCAST_INTERVAL_MS - elapsed;
      setCountdownText(formatCountdown(Math.max(0, remaining)));
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [lastUpdated]);

  // Handle keyboard
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'KeyB' || e.code === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
    pointerEvents: 'auto',
  };

  const modalStyle: React.CSSProperties = {
    width: '500px',
    maxWidth: '90vw',
    height: '500px',
    maxHeight: '80vh',
    backgroundColor: PALETTE.PANEL_TRANSPARENT,
    border: `2px solid ${PALETTE.ORANGE}`,
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: "'JetBrains Mono', monospace",
    color: PALETTE.LIGHT,
    overflow: 'hidden',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px 20px',
    borderBottom: `1px solid ${PALETTE.DIM}`,
    backgroundColor: PALETTE.DARK_TRANSPARENT,
  };

  const backButtonStyle: React.CSSProperties = {
    padding: '6px 12px',
    fontSize: '11px',
    backgroundColor: PALETTE.PANEL,
    color: PALETTE.LIGHT,
    border: `1px solid ${PALETTE.DIM}`,
    borderRadius: '4px',
    cursor: 'pointer',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: 600,
    color: PALETTE.ORANGE,
    flex: 1,
  };

  const subtitleStyle: React.CSSProperties = {
    padding: '12px 20px',
    borderBottom: `1px solid ${PALETTE.DIM}`,
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
  };

  const contentStyle: React.CSSProperties = {
    flex: 1,
    padding: '16px 20px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  };

  const bountyCardStyle: React.CSSProperties = {
    backgroundColor: PALETTE.DARK_TRANSPARENT,
    border: `1px solid ${PALETTE.DIM}`,
    borderRadius: '6px',
    padding: '12px 16px',
  };

  const bountyHeaderStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  };

  const playerNameStyle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: 600,
    color: PALETTE.RED,
  };

  const bountyInfoStyle: React.CSSProperties = {
    fontSize: '11px',
    color: PALETTE.DIM,
    lineHeight: '1.6',
  };

  const bountyAmountStyle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: 600,
    color: PALETTE.YELLOW,
    marginTop: '8px',
  };

  const emptyStateStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    color: PALETTE.DIM,
    padding: '40px 20px',
  };

  const footerStyle: React.CSSProperties = {
    padding: '12px 20px',
    borderTop: `1px solid ${PALETTE.DIM}`,
    textAlign: 'center',
    fontSize: '11px',
    color: PALETTE.DIM,
    backgroundColor: PALETTE.DARK_TRANSPARENT,
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <button style={backButtonStyle} onClick={onClose}>
            ← Back
          </button>
          <span style={titleStyle}>BOUNTY BOARD</span>
        </div>

        {/* Subtitle */}
        <div style={subtitleStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: PALETTE.RED, fontSize: '11px' }}>WANTED CRIMINALS</span>
            <span style={{ color: PALETTE.TEAL, fontSize: '11px', marginLeft: 'auto' }}>
              Next broadcast in {countdownText}
            </span>
          </div>
        </div>

        {/* Content */}
        <div style={contentStyle}>
          {bounties.length === 0 ? (
            <div style={emptyStateStyle}>
              <p style={{ fontSize: '11px', marginBottom: '8px' }}>No active bounties at this time.</p>
              <p style={{ fontSize: '11px' }}>The constellation is peaceful... for now.</p>
            </div>
          ) : (
            bounties.map((bounty) => (
              <div key={bounty.playerId} style={bountyCardStyle}>
                <div style={bountyHeaderStyle}>
                  <span style={{ fontSize: '11px' }}>🎯</span>
                  <span style={playerNameStyle}>{bounty.playerName}</span>
                </div>
                <div style={bountyInfoStyle}>
                  <div>
                    <span style={{ color: PALETTE.DIM }}>Last seen: </span>
                    <span style={{ color: PALETTE.LIGHT }}>{bounty.lastConstellationName}</span>
                  </div>
                  <div>
                    <span style={{ color: PALETTE.DIM }}>Ship: </span>
                    <span style={{ color: getTierColor(bounty.lastShipTier) }}>
                      {getShipName(bounty.lastShipClass)}
                    </span>
                    <span style={{ color: PALETTE.DIM }}> (T{bounty.lastShipTier} {getShipClassName(bounty.lastShipClass)})</span>
                  </div>
                </div>
                <div style={bountyAmountStyle}>
                  Bounty: {formatCredits(bounty.amount)} CR
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          Press [B] to close
        </div>
      </div>
    </div>
  );
}

export default BountyBoardModal;
